import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowRight } from 'lucide-react';
import type { Article } from '../../types/blog';
import SiloBadge from './SiloBadge';

interface ArticleCardProps {
  article: Article;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article }) => {
  const coverImage = article.coverImage ?? '/images/heroes/blog-hero.webp';
  const coverAlt = article.coverImageAlt ?? `Illustration de l'article : ${article.h1}`;

  return (
    <motion.div
      className="group relative flex flex-col overflow-hidden card-hover h-full"
    >
      <div className="h-px w-full bg-line" />
      <div className="relative aspect-[16/9] overflow-hidden border-b border-line">
        <img
          src={coverImage}
          alt={coverAlt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-surface/60 via-transparent to-transparent" />
      </div>

      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-center justify-between gap-2">
          <SiloBadge silo={article.silo} />
          {article.isPillar && (
            <span className="rounded-full border border-accent/35 bg-accent-strong/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-accent-soft">
              Pilier
            </span>
          )}
        </div>

        <h2 className="text-fg font-semibold text-base leading-snug group-hover:text-accent-soft transition-colors line-clamp-2">
          {article.h1}
        </h2>

        <p className="text-fg-muted text-sm leading-relaxed line-clamp-3 flex-1">
          {article.excerpt}
        </p>

        <div className="flex items-center justify-between mt-auto pt-3 border-t border-line">
          <span className="flex items-center gap-1.5 text-fg-subtle text-xs">
            <Clock className="h-3.5 w-3.5" />
            {article.readingMinutes} min de lecture
          </span>
          <Link
            to={`/blog/${article.slug}`}
            className="inline-flex min-h-11 items-center gap-1.5 text-accent-soft hover:text-accent-soft text-sm font-medium transition-colors md:min-h-0"
          >
            Lire l'article <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default ArticleCard;
